// Access (/internal/access, VRG-727) — the small dialogs behind the row kebab and the
// "shown once" link dialog shared with the Invite form.
//
// Built from the house components only (§28/§31): `@repo/ui` Modal / Button / Input /
// Alert, the platform `SelectField` + `DatePickerField` (the ExtendDemoModal expiry
// pattern), and `CheckboxRow` for options. MUI appears only for layout (Stack,
// Typography) and the copy IconButton the CreateDemoModal link field already uses.
import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { useForm } from 'react-hook-form'
import { IconButton, Stack, Typography } from '@mui/material'
import { CheckIcon, CopyIcon } from 'lucide-react'
import { Alert, Button, Input, Modal } from '@repo/ui'

import { CheckboxRow, DatePickerField, SelectField } from 'components'
import type { GrantResult } from 'hooks/api/usePeople'

const DAY_MS = 24 * 60 * 60 * 1000
/** Mirrors the API's SITE_GATE_INVITE_MAX_DAYS. The API re-checks. */
export const MAX_EXPIRY_DAYS = 90

export const EXPIRY_OPTIONS = [
  { label: '7 days', value: '7' },
  { label: '14 days', value: '14' },
  { label: '30 days', value: '30' },
  { label: 'Custom date', value: 'custom' },
]

const DATE_FORMAT = 'YYYY-MM-DD'

export type ExpiryValue = { preset: string; custom: string }
export const DEFAULT_EXPIRY: ExpiryValue = { preset: '14', custom: '' }

/** ISO expiry, or an error message. A custom date means "through the end of that day". */
export const resolveExpiry = (v: ExpiryValue): { iso: string } | { error: string } => {
  if (v.preset !== 'custom') return { iso: new Date(Date.now() + Number(v.preset) * DAY_MS).toISOString() }
  if (!v.custom) return { error: 'Pick a date' }
  const end = dayjs(v.custom).endOf('day')
  if (!end.isValid()) return { error: 'Pick a date' }
  if (end.isBefore(dayjs().add(1, 'hour')) || end.isAfter(dayjs().add(MAX_EXPIRY_DAYS, 'day').endOf('day'))) {
    return { error: `Pick a date between today and ${MAX_EXPIRY_DAYS} days from now` }
  }
  return { iso: end.toDate().toISOString() }
}

/**
 * Expiry: the platform `SelectField` (7 / 14 / 30 days / Custom date) and, for a custom
 * date, the platform `DatePickerField` — the same pair ExtendDemoModal uses for a demo's
 * lifetime. DatePickerField is a react-hook-form field, so it gets a form of its own and
 * reports up through `onValueChange`.
 */
export const ExpiryField = ({
  value,
  onChange,
  error,
  label,
}: {
  value: ExpiryValue
  onChange: (v: ExpiryValue) => void
  error?: string
  label?: string
}) => {
  const methods = useForm<{ customDate: string }>({ defaultValues: { customDate: value.custom } })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const untypedFieldMethods: any = methods
  return (
    <Stack spacing={3}>
      <SelectField label={label} value={value.preset} onChange={(p) => onChange({ ...value, preset: p as string })} options={EXPIRY_OPTIONS} />
      {value.preset === 'custom' && (
        <Stack spacing={1}>
          <DatePickerField
            {...untypedFieldMethods}
            name="customDate"
            label="Expires on"
            format={DATE_FORMAT}
            minDate={dayjs()}
            maxDate={dayjs().add(MAX_EXPIRY_DAYS, 'day')}
            onValueChange={(v: string | null) => onChange({ ...value, custom: v ?? '' })}
          />
          {error && (
            <Typography variant="body4" color="error">
              {error}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  )
}

/** An access option: the shared `CheckboxRow` (body3 label, body4 description). */
export const Option = ({
  label,
  hint,
  checked,
  onChange,
  disabled,
  indent = false,
}: {
  name?: string
  label: string
  hint?: string
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
  indent?: boolean
}) => <CheckboxRow label={label} description={hint} checked={checked} onChange={onChange} disabled={disabled} indent={indent} />

const CopyRow = ({ label, url, caption }: { label: string; url: string; caption: string }) => {
  const [copied, setCopied] = useState(false)
  const copy = () => {
    void navigator.clipboard.writeText(url)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }
  return (
    <Stack spacing={1}>
      <Input
        label={label}
        value={url}
        readOnly
        endAdornment={
          <IconButton aria-label={`Copy ${label.toLowerCase()}`} onClick={copy} size="small">
            {copied ? <CheckIcon strokeWidth={1} size={18} /> : <CopyIcon strokeWidth={1} size={18} />}
          </IconButton>
        }
      />
      <Typography variant="body3" color="text.secondary">
        {caption}
      </Typography>
    </Stack>
  )
}

/** After an invite or a grant: every link to copy, and what (if anything) was emailed. */
export const LinksModal = ({ result, name, onClose }: { result: GrantResult | null; name: string; onClose: () => void }) => {
  const emailed = result?.emails_sent ?? []
  return (
    <Modal
      title="Links to send"
      open={Boolean(result)}
      onClose={onClose}
      actions={<Button onClick={onClose}>Done</Button>}
    >
      <Stack spacing={4} mt={2}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {emailed.length === 0
            ? <>Nothing was emailed to <strong>{name}</strong>. Copy the links below and send them yourself.</>
            : <>Emailed to <strong>{name}</strong>: {emailed.map((e) => (e === 'pricing' ? 'pricing link' : 'platform invite')).join(' and ')}.</>}
        </Typography>
        {result?.website_url && (
          <CopyRow label="Website link" url={result.website_url} caption="Opens vera.inc without the password. Shown once: only a fingerprint is stored." />
        )}
        {result?.pricing_url && (
          <CopyRow label="Pricing link" url={result.pricing_url} caption="Opens the real price list. Works once, for 7 days. Shown once." />
        )}
        {result?.platform_url && (
          <CopyRow label="Platform signup link" url={result.platform_url} caption="Lets them create a Vera account. You can copy this again from the row menu." />
        )}
        {result?.website_url || result?.pricing_url ? (
          <Alert severity="warning" description="Website and pricing links cannot be shown again. If one is lost, regenerate it from the row menu." />
        ) : null}
      </Stack>
    </Modal>
  )
}

/** Grant pricing or platform access, with the explicit, unticked email box. */
export const GrantDialog = ({
  kind,
  name,
  open,
  loading,
  onClose,
  onConfirm,
}: {
  kind: 'pricing' | 'platform'
  name: string
  open: boolean
  loading: boolean
  onClose: () => void
  onConfirm: (sendEmail: boolean) => void
}) => {
  const [send, setSend] = useState(false)
  useEffect(() => {
    if (open) setSend(false)
  }, [open])
  return (
    <Modal
      title={kind === 'pricing' ? 'Grant pricing access?' : 'Grant platform access?'}
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button loading={loading} onClick={() => onConfirm(send)}>
            Grant
          </Button>
        </>
      }
    >
      <Stack spacing={4} mt={2}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {kind === 'pricing'
            ? <><strong>{name}</strong> can open Vera&apos;s real price list. You&apos;ll get a single-use link (7 days) to send them.</>
            : <><strong>{name}</strong> can create a Vera account (and see pricing once signed in). You&apos;ll get their signup link to send them.</>}
        </Typography>
        <Option
          name={`send-${kind}-email`}
          label={kind === 'pricing' ? 'Send pricing email' : 'Send invite email'}
          hint="Off by default. Leave it off to send the link yourself."
          checked={send}
          onChange={setSend}
        />
      </Stack>
    </Modal>
  )
}

/** Create website access, or move an existing link's expiry. */
export const ExpiryDialog = ({
  mode,
  name,
  open,
  loading,
  onClose,
  onConfirm,
}: {
  mode: 'create' | 'edit'
  name: string
  open: boolean
  loading: boolean
  onClose: () => void
  onConfirm: (expiresAtIso: string) => void
}) => {
  const [value, setValue] = useState<ExpiryValue>(DEFAULT_EXPIRY)
  const [error, setError] = useState<string>()
  useEffect(() => {
    if (open) {
      setValue(DEFAULT_EXPIRY)
      setError(undefined)
    }
  }, [open])
  const confirm = () => {
    const r = resolveExpiry(value)
    if ('error' in r) return setError(r.error)
    onConfirm(r.iso)
  }
  return (
    <Modal
      title={mode === 'create' ? 'Create website link' : 'Change website expiry'}
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button loading={loading} onClick={confirm}>
            {mode === 'create' ? 'Create link' : 'Save'}
          </Button>
        </>
      }
    >
      <Stack spacing={4} mt={2}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {mode === 'create'
            ? <>A personal link that lets <strong>{name}</strong> past the vera.inc password screen. Any live link they have is retired.</>
            : <>The link for <strong>{name}</strong> keeps working until the new date.</>}
        </Typography>
        <ExpiryField value={value} onChange={setValue} error={error} />
      </Stack>
    </Modal>
  )
}

export const NoteDialog = ({
  name,
  initial,
  open,
  loading,
  onClose,
  onSave,
}: {
  name: string
  initial: string
  open: boolean
  loading: boolean
  onClose: () => void
  onSave: (note: string) => void
}) => {
  const [note, setNote] = useState(initial)
  useEffect(() => {
    if (open) setNote(initial)
  }, [open, initial])
  return (
    <Modal
      title={initial ? 'Edit note' : 'Add note'}
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button loading={loading} onClick={() => onSave(note)}>
            Save
          </Button>
        </>
      }
    >
      <Stack spacing={2} mt={2}>
        <Typography variant="body2" color="text.secondary">
          Staff only. About <strong>{name}</strong>.
        </Typography>
        <Input
          value={note}
          multiline
          minRows={3}
          onValueChange={(v: string) => setNote(v)}
          placeholder="Met at the Paris show; follow up after the demo"
        />
      </Stack>
    </Modal>
  )
}
